package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.kyd.CoordinateSystem
import uk.co.nickthecoder.kyd.Vector2Property
import uk.co.nickthecoder.kyd.util.Vector2
import kotlin.math.max
import kotlin.math.min

/**
 * Appearances whose bounds can be defined by two opposite corners of a rectangle, both of which can be moved anywhere.
 *
 * [Pose] and [Graphic] are not [StretchableAppearance], because the corners cannot be arbitrarily changed,
 * they are defined by the `pin` and the size of the pose/graphic.
 *
 * `corner1` and `corner3` are derived from [corner0] and [corner2] and therefore are not stored, and aren't mutable.
 *
 * The rectangular region will be centered iff [corner0] == - [corner2]
 *
 * In some cases, it is useful to grow/shrink an appearance by changing the position of the corners AND to change the
 * actor's scale.
 * For example, suppose we have a [NinePatchAppearance] of a framed picture. We can change the
 * size of the picture by changing [corner0] and [corner2]; the picture frame's thickness wouldn't change.
 * If we want a thicker frame, scale the actor. We would also need to move the corners to compensate if
 * we wanted the overall size framed picture to remain unchanged.
 */
interface StretchableAppearance : Appearance {

    val corner0Property: Vector2Property
    /**
     * In conjunction with [corner2], this defines a rectangle in [CoordinateSystem.local] coordinates.
     */
    var corner0: Vector2

    /**
     * A corner of the bounding rectangle derived from [corner0] and [corner2].
     */
    val corner1: Vector2
        get() = Vector2(corner2.x, corner0.y)

    /**
     * In conjunction with [corner2], this defines a rectangle in [CoordinateSystem.local] coordinates.
     */
    val corner2Property: Vector2Property
    var corner2: Vector2

    /**
     * A corner of the bounding rectangle derived from [corner0] and [corner2].
     */
    val corner3: Vector2
        get() = Vector2(corner0.x, corner2.y)

    val resourcePath: String

    override fun localCorners(): List<Vector2> {
        return listOf(corner0, corner1, corner2, corner3)
    }

    override fun localBounds() = Pair(corner0, corner2)

    override fun containsLocalPoint(localPoint: Vector2): Boolean {
        if (localPoint.x < min(corner0.x, corner2.x)) return false
        if (localPoint.x > max(corner0.x, corner2.x)) return false
        if (localPoint.y < min(corner0.y, corner2.y)) return false
        return localPoint.y <= max(corner0.y, corner2.y)
    }

    fun moveCorner(corner: Int, newLocalPosition: Vector2) {
        when (corner) {
            0 -> corner0 = newLocalPosition
            2 -> corner2 = newLocalPosition
            1 -> {
                corner0 = Vector2(corner0.x, newLocalPosition.y)
                corner2 = Vector2(newLocalPosition.x, corner2.y)
            }

            3 -> {
                corner0 = Vector2(newLocalPosition.x, corner0.y)
                corner2 = Vector2(corner2.x, newLocalPosition.y)
            }

            else -> {
                throw IllegalArgumentException("corner must be in the range 0..3")
            }
        }
    }
}
